import { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Plus, X, Trash2, Ruler, ToggleLeft } from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import { Cargando } from '../ui/EstadoDePantalla';

// QUÉ SE REVISA DE CADA PRODUCTO · Fase 3 del módulo de Calidad.
//
// SE LLAMA ASÍ Y NO «PLANES DE INSPECCIÓN» a propósito. «Plan de
// inspección» es como lo llama la norma; «qué se revisa de cada
// producto» es como lo pregunta la persona que entra aquí por primera
// vez, y esta pantalla la va a abrir alguien que no ha leído la norma.
// Dentro se usa la palabra «plan», que sí es la que se dice en voz alta
// en una planta.
//
// TRES DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **Dar de alta un plan incluye sus características.** Un plan
//      vacío no revisa nada y obligaría a encontrar una segunda pantalla
//      para que empezara a servir.
//   2. **Cada característica se explica en su sitio.** «Nominal» y
//      «tolerancia» no significan lo mismo para todo el mundo, y aquí
//      significan una cosa muy concreta: la tolerancia es una DESVIACIÓN
//      respecto al nominal, como viene en un plano. Se dice debajo del
//      campo, no en un manual.
//   3. **El botón deshabilitado dice por qué.** Un botón gris sin
//      explicación deja a la persona probando cosas.

const TIPOS = [
  ['RECIBO', 'De recibo — al material que llega'],
  ['PROCESO', 'En proceso — mientras se fabrica'],
  ['FINAL', 'Final — antes de embarcar'],
];

function caracteristicaVacia() {
  return {
    nombre: '', clase: 'VARIABLE', nominal: '', tolerancia_inferior: '',
    tolerancia_superior: '', unidad: '', instrumento_requerido: '',
    tamano_muestra: 1, frecuencia: '',
  };
}

// ---------------------------------------------------------------------
// Un renglón del plan
// ---------------------------------------------------------------------
function Caracteristica({ valor, indice, onCambiar, onQuitar, puedeQuitar }) {
  const set = (campo) => (e) => onCambiar(indice, { ...valor, [campo]: e.target.value });

  return (
    <div className="border border-gray-200 rounded-xl p-3 space-y-3 bg-gray-50">
      <div className="flex items-start gap-2">
        <span className="mt-2 text-xs font-semibold text-gray-500 w-6 shrink-0">
          {indice + 1}
        </span>
        <label className="block flex-1">
          <span className="block text-sm font-medium text-gray-700">
            Qué se revisa
          </span>
          <input
            value={valor.nombre}
            onChange={set('nombre')}
            placeholder="Diámetro exterior"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <span className="block text-xs text-gray-500 mt-1">
            Como lo va a leer quien mida, con la pieza en la mano.
          </span>
        </label>
        {puedeQuitar && (
          <button type="button" onClick={() => onQuitar(indice)}
                  className="mt-7 text-gray-400 hover:text-red-600"
                  aria-label={`Quitar ${valor.nombre || 'esta característica'}`}>
            <Trash2 size={18} />
          </button>
        )}
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Cómo se comprueba</span>
        <select value={valor.clase} onChange={set('clase')}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="VARIABLE">Se mide y da un número (un diámetro, un peso)</option>
          <option value="ATRIBUTO">Se mira y da un sí o un no (¿tiene rebaba?)</option>
        </select>
      </label>

      {valor.clase === 'VARIABLE' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Cuánto debe medir
              </span>
              <input
                type="number" step="any" value={valor.nominal} onChange={set('nominal')}
                placeholder="10"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Unidad</span>
              <input
                value={valor.unidad} onChange={set('unidad')} placeholder="mm"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Cuánto puede quedarse corto
              </span>
              <input
                type="number" step="any" value={valor.tolerancia_inferior}
                onChange={set('tolerancia_inferior')} placeholder="-0.1"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Cuánto puede pasarse
              </span>
              <input
                type="number" step="any" value={valor.tolerancia_superior}
                onChange={set('tolerancia_superior')} placeholder="0.1"
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Se escriben como vienen en el plano: una desviación respecto a lo
            que debe medir. Con 10 y «−0.1 / +0.1», vale de 9.9 a 10.1. Si las
            dejas en blanco, cualquier medida se da por buena.
          </p>
        </>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">
            Con qué se mide
          </span>
          <input
            value={valor.instrumento_requerido} onChange={set('instrumento_requerido')}
            placeholder="Vernier"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Cada cuánto</span>
          <input
            value={valor.frecuencia} onChange={set('frecuencia')}
            placeholder="Cada tarima"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Alta de un plan
// ---------------------------------------------------------------------
function FormularioAlta({ onCreado, onCancelar }) {
  const [sku, setSku] = useState('');
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('RECIBO');
  const [caracteristicas, setCaracteristicas] = useState([caracteristicaVacia()]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  function cambiar(indice, nueva) {
    setCaracteristicas((antes) => antes.map((c, i) => (i === indice ? nueva : c)));
  }
  function quitar(indice) {
    setCaracteristicas((antes) => antes.filter((_, i) => i !== indice));
  }

  // VALIDACIÓN EN VIVO, no al guardar (Doctrina 2, regla 8). Se dice UNA
  // cosa —la primera que falta— y no una lista: quien está llenando esto
  // arregla de una en una.
  const nombres = caracteristicas.map((c) => c.nombre.trim().toUpperCase());
  const repetido = nombres.find((n, i) => n && nombres.indexOf(n) !== i);
  const sinNominal = caracteristicas.find(
    (c) => c.clase === 'VARIABLE' && c.nombre.trim() && String(c.nominal).trim() === '');
  const alReves = caracteristicas.find(
    (c) => c.tolerancia_inferior !== '' && c.tolerancia_superior !== ''
      && Number(c.tolerancia_inferior) > Number(c.tolerancia_superior));

  const falta = !sku.trim() ? 'el código del producto'
    : nombre.trim().length < 2 ? 'el nombre del plan'
    : !caracteristicas.some((c) => c.nombre.trim()) ? 'al menos una cosa que revisar'
    : repetido ? `«${repetido.toLowerCase()}» está dos veces`
    : sinNominal ? `cuánto debe medir «${sinNominal.nombre}»`
    : alReves ? `las tolerancias de «${alReves.nombre}» están al revés`
    : null;

  function limpiar(c) {
    const salida = {
      nombre: c.nombre.trim(),
      clase: c.clase,
      tamano_muestra: Number(c.tamano_muestra) || 1,
    };
    if (c.unidad.trim()) salida.unidad = c.unidad.trim();
    if (c.instrumento_requerido.trim()) {
      salida.instrumento_requerido = c.instrumento_requerido.trim();
    }
    if (c.frecuencia.trim()) salida.frecuencia = c.frecuencia.trim();
    if (c.clase === 'VARIABLE') {
      salida.nominal = Number(c.nominal);
      if (String(c.tolerancia_inferior).trim() !== '') {
        salida.tolerancia_inferior = Number(c.tolerancia_inferior);
      }
      if (String(c.tolerancia_superior).trim() !== '') {
        salida.tolerancia_superior = Number(c.tolerancia_superior);
      }
    }
    return salida;
  }

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const d = await api.post('/inspecciones/planes', {
        sku, nombre, tipo,
        caracteristicas: caracteristicas.filter((c) => c.nombre.trim()).map(limpiar),
      });
      onCreado(d);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-4 bg-white">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Qué se le revisa a un producto</h3>
        <button type="button" onClick={onCancelar}
                className="text-gray-500 hover:text-gray-700" aria-label="Cancelar">
          <X size={18} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Producto</span>
          <input
            value={sku} onChange={(e) => setSku(e.target.value)} placeholder="TUB-1020"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <span className="block text-xs text-gray-500 mt-1">
            El código con el que está dado de alta en Productos.
          </span>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Cuándo se aplica</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {TIPOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Nombre del plan</span>
        <input
          value={nombre} onChange={(e) => setNombre(e.target.value)}
          placeholder="Inspección de entrada"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </label>

      <div className="space-y-3">
        <div className="text-sm font-semibold text-gray-900">Qué se revisa</div>
        {caracteristicas.map((c, i) => (
          <Caracteristica
            key={i} valor={c} indice={i} onCambiar={cambiar} onQuitar={quitar}
            puedeQuitar={caracteristicas.length > 1}
          />
        ))}
        <button
          type="button"
          onClick={() => setCaracteristicas((a) => [...a, caracteristicaVacia()])}
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-marca)] hover:underline"
        >
          <Plus size={16} /> Añadir otra cosa que revisar
        </button>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          type="submit" disabled={!!falta || guardando}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar el plan'}
        </button>
        {falta && <span className="text-sm text-gray-500">Falta {falta}.</span>}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------
export default function PlanesInspeccion() {
  const [planes, setPlanes] = useState(null);
  const [error, setError] = useState(null);
  const [alta, setAlta] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [verRetirados, setVerRetirados] = useState(false);

  // Sin `async` en el cuerpo del efecto, que es el patrón de la casa
  // (`Documentos.jsx`): lo que carga se resuelve en el `then`.
  const cargar = useCallback(() => {
    api.get(`/inspecciones/planes${verRetirados ? '?incluir_retirados=true' : ''}`)
      .then((d) => setPlanes(d.planes))
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, [verRetirados]);

  useEffect(cargar, [cargar]);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold text-gray-900">
          Qué se revisa de cada producto
        </h1>
        <p className="text-sm text-gray-600">
          La lista de cosas que hay que comprobar en cada producto, con cuánto
          deben medir. Es contra esto que se juzga cada inspección.
        </p>
      </header>

      {aviso && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {alta ? (
        <FormularioAlta
          onCancelar={() => setAlta(false)}
          onCreado={(d) => { setAlta(false); setAviso(d.mensaje); cargar(); }}
        />
      ) : (
        <button
          onClick={() => { setAlta(true); setAviso(null); }}
          className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
        >
          <Plus size={16} /> Dar de alta un plan
        </button>
      )}

      {planes === null && <Cargando filas={4} />}

      {planes !== null && planes.length === 0 && !alta && (
        <EstadoVacio
          icono={ClipboardList}
          titulo="Todavía no has dicho qué se revisa de nada"
          descripcion={
            'Un plan es la lista de cosas que hay que comprobarle a un '
            + 'producto: qué se mide, cuánto debe medir y cuánto puede '
            + 'desviarse. Sin uno, no hay contra qué comparar lo que alguien '
            + 'mida en el andén.'
          }
          accion={(
            <button
              onClick={() => setAlta(true)}
              className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
            >
              <Plus size={16} /> Dar de alta el primero
            </button>
          )}
        />
      )}

      {planes !== null && planes.length > 0 && (
        <>
          <NotaExplicativa titulo="Cambiar un plan no cambia lo que ya se midió">
            Cada inspección se guarda su propia copia del plan tal como estaba
            ese día. Si mañana aprietas una tolerancia, las inspecciones de la
            semana pasada siguen diciendo exactamente lo que se firmó.
          </NotaExplicativa>

          <div className="space-y-2">
            {planes.map((p) => (
              <div key={p.id}
                   className="border border-gray-200 rounded-xl p-3 bg-white flex items-start gap-3">
                <Ruler className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900">
                    {p.sku} · {p.nombre}
                  </div>
                  <div className="text-sm text-gray-600">
                    {p.tipo_nombre} · versión {p.version}
                    {p.cliente ? ` · solo para ${p.cliente}` : ''}
                  </div>
                  <div className="text-sm text-gray-600">
                    Revisa {p.caracteristicas}{' '}
                    {p.caracteristicas === 1 ? 'cosa' : 'cosas'}
                    {p.caracteristicas === 0 && ' — todavía no sirve para nada'}
                  </div>
                </div>
                {!p.activo && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                    Retirado
                  </span>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => setVerRetirados((v) => !v)}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline"
          >
            <ToggleLeft size={16} />
            {verRetirados ? 'Ver solo los que están en uso' : 'Ver también los retirados'}
          </button>
        </>
      )}
    </div>
  );
}
